import { QueryClientProvider } from "@tanstack/react-query";
import type { FC } from "react";
import { type Root, createRoot } from "react-dom/client";
import { defaultQueryClient } from "./default-query-client";
import { lazify } from "./lazify";

export abstract class BridgeElement extends HTMLElement {
	abstract Component: FC;
	_root: Root | undefined;

	get renderRoot() {
		return this.shadowRoot ?? (this as HTMLElement);
	}

	connectedCallback() {
		this._root = createRoot(this.renderRoot);
		this.render();
	}

	disconnectedCallback() {
		if (this._root) {
			this._root.unmount();
			this._root = undefined;
		}
	}

	render() {
		if (this.isConnected && this._root) {
			const { Component } = this;
			this._root.render(
				<QueryClientProvider client={defaultQueryClient}>
					<Component />
				</QueryClientProvider>,
			);
		}
	}
}

export function defineBridgeElement(
	tagName: string,
	Component: FC,
	shadowRootInit?: ShadowRootInit,
) {
	customElements.define(
		tagName,
		class extends BridgeElement {
			static tagName = tagName;
			Component = Component;

			constructor() {
				super();
				if (shadowRootInit) {
					this.attachShadow(shadowRootInit);
				}
			}
		},
	);
}

export function lazyBridgeElement(
	tagName: string,
	dynamicImport: () => Promise<{ default: FC }>,
) {
	defineBridgeElement(tagName, lazify(dynamicImport));
}
